import { ApolloClient, ApolloLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import { setContext } from '@apollo/client/link/context'

// import { BatchHttpLink } from '@apollo/client/link/batch-http'

import { buildAxiosFetch } from '@lifeomic/axios-fetch'
import axios from 'axios'

const isObject = (node) => typeof node === 'object' && node !== null


// Ensure CSRF cookie exists and attach token header on first GraphQL request
const readCookie = (name) => {
	const match = document.cookie.split('; ').find((row) => row.startsWith(name + '='))
	return match ? decodeURIComponent(match.split('=')[1]) : null
}

const ensureCsrf = async (API_URL=process.env.ACQUIRE_API_URL) => {
	if (!readCookie('X-CSRF-Token')) {
		try {
			await axios({ method: 'GET', url: `${API_URL}/csrf`, withCredentials: true, maxRedirects: 0 })
		} catch (e) {
			// swallow; server will also set cookie on 403 responses, but we proactively seed it
		}
	}
	return readCookie('X-CSRF-Token')
}

const csrfLink = setContext(async (_, { headers }) => {
	const token = await ensureCsrf()
	return {
		headers: {
			...headers,
			...(token ? { 'X-CSRF-Token': token } : {}),
		},
	}
})

// rough first draft, could probably be optimised in a loads of different ways.
// eslint-disable-next-line no-unused-vars
const hasFiles = (node, found = []) => {
	Object.keys(node).forEach((key) => {
		if (!isObject(node[key]) || found.length > 0) {
			return
		}

		if (
			(typeof File !== 'undefined' && node[key] instanceof File) ||
			(typeof Blob !== 'undefined' && node[key] instanceof Blob)
		) {
			found.push(node[key])
			return
		}

		hasFiles(node[key], found)
	})

	return found.length > 0
}

const client = new ApolloClient({
	link: ApolloLink.from([
		onError(({ graphQLErrors, networkError }) => {
			if (graphQLErrors)
				graphQLErrors.forEach(({ message, locations, path }) =>
					console.error(
						`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
						locations,
					),
				)
			if (networkError) console.error(`[Network error]: ${networkError}`)
		}),
		createUploadLink({
			uri: `${process.env.API_URL}/graphql`,
			fetch: buildAxiosFetch(axios),
		}),
		// }).split(
		// 	({ variables }) => hasFiles(variables),
		// 	createUploadLink({
		// 		uri: `${process.env.API_URL}/graphql`,
		// 		fetch: buildAxiosFetch(axios),
		// 	}),
		// 	new BatchHttpLink({
		// 		uri: `${process.env.API_URL}/graphql`,
		// 		fetch: buildAxiosFetch(axios),
		// 	}),
		// ),
	]),
	cache: new InMemoryCache(),
	shouldBatch: false,
	defaultOptions: {
		query: {
			fetchPolicy: 'cache-and-network',
		},
	},
})

const prospectix_client = new ApolloClient({
	link: ApolloLink.from([
		onError(({ graphQLErrors, networkError }) => {
			if (graphQLErrors)
				graphQLErrors.forEach(({ message, locations, path }) =>
					console.error(
						`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
						locations,
					),
				)
			if (networkError) console.error(`[Network error]: ${networkError}`)
		}),
		createUploadLink({
			uri: `${process.env.PROSPECTIX_URL}/graphql`,
			fetch: buildAxiosFetch(axios),
		}),
		// new HttpLink({
		// 	uri: `${process.env.PROSPECTIX_URL}/graphql`,
		// 	fetch: buildAxiosFetch(axios),
		// }),
	]),
	cache: new InMemoryCache(),
	shouldBatch: false,
	defaultOptions: {
		query: {
			fetchPolicy: 'cache-and-network',
		},
	},
})

const acquire_app_client = new ApolloClient({
	link: ApolloLink.from([
		csrfLink,
		onError(({ graphQLErrors, networkError }) => {
			console.info('acquire_app_client onError called')
			console.info({ graphQLErrors, networkError })
			if (graphQLErrors) {
				graphQLErrors.forEach(({ message, locations, path }) =>
					console.error(
						`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
						locations,
					),
				)

				// Let CSRF 403 be handled by axios retry before notifying
				const onlyCsrf403 = !networkError && graphQLErrors.some(({ extensions }) => extensions?.code === 'FORBIDDEN')
				console.info('onlyCsrf403', onlyCsrf403)
				if (!onlyCsrf403) {
					console.error(
						<span>
							Errors:{' '}
							{graphQLErrors.map(({ message }) => (
								<React.Fragment>
									<br />
									{message}
								</React.Fragment>
							))}
						</span>,
					)
				}
			}
			if (networkError) {
				const status = networkError?.statusCode || networkError?.status || networkError?.response?.status
				if (status === 403) return // suppress CSRF 403; axios interceptor will retry

				const json_error_message = String(networkError)
				if (json_error_message.includes('Unexpected end of JSON input') || 
					json_error_message.includes('ServerParseError')) {
					console.error(`[Network error]: ${networkError}`)
					return // suppress "Unexpected end of JSON input" errors
				}
				
				console.error(`[Network error]: ${networkError}`)
			}
		}),
		createUploadLink({
			uri: `${process.env.ACQUIRE_API_URL}/graphql`,
			fetch: buildAxiosFetch(axios),
			credentials: 'include',
		}),
		// new HttpLink({
		// 	uri: `${process.env.PROSPECTIX_URL}/graphql`,
		// 	fetch: buildAxiosFetch(axios),
		// }),
	]),
	cache: new InMemoryCache(),
	shouldBatch: false,
	defaultOptions: {
		query: {
			fetchPolicy: 'cache-and-network',
		},
	},
})

export default client

export { client, prospectix_client, acquire_app_client }
